import type { ReactNode } from 'react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { externalIntegrationsService } from '@/features/external-integrations/services/external-integrations.service';
import { useToast } from '@/hooks/use-toast';
import {
  Check,
  Copy,
  BookOpen,
  Key,
  Loader2,
  Plug,
  Trash2,
} from 'lucide-react';

const API_BASE = '/api/integrations/v1';

export const Route = createFileRoute('/_authenticated/external-integrations')({
  component: ExternalIntegrationsPage,
});

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
      {children}
    </code>
  );
}
function ExternalIntegrationsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['external-integrations-api-key'],
    queryFn: () => externalIntegrationsService.getApiKeyStatus(),
  });

  const generateMutation = useMutation({
    mutationFn: () => externalIntegrationsService.generateApiKey(),
    onSuccess: (result) => {
      setRevealedKey(result.apiKey);
      queryClient.invalidateQueries({
        queryKey: ['external-integrations-api-key'],
      });
      toast({
        title: 'API key gerada',
        description: 'Copie a chave agora — ela não será mostrada novamente.',
      });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao gerar API key',
        variant: 'destructive',
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => externalIntegrationsService.revokeApiKey(),
    onSuccess: () => {
      setRevealedKey(null);
      queryClient.invalidateQueries({
        queryKey: ['external-integrations-api-key'],
      });
      toast({ title: 'API key revogada' });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao revogar API key',
        variant: 'destructive',
      });
    },
  });

  const copyKey = async () => {
    if (!revealedKey) return;
    await navigator.clipboard.writeText(revealedKey);
    setCopied(true);
    toast({ title: 'API key copiada' });
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const busy = generateMutation.isPending || revokeMutation.isPending;

  return (
    <PermissionGate permission={Permission.AI_ANALYSIS_MANAGE}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Integrações Externas</h1>
          <p className="mt-2 text-muted-foreground">
            Gere uma API key por tenant para o Lovable (ou outro sistema)
            sincronizar as análises completas de boards e WhatsApp.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5" />
              API key do tenant
            </CardTitle>
            <CardDescription>
              Autentique as requisições com o header <Code>x-api-key</Code>.
              Veja os endpoints disponíveis na documentação abaixo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              {status?.hasKey && status.isActive ? (
                <Badge className="bg-green-600 hover:bg-green-600">Ativa</Badge>
              ) : status?.hasKey ? (
                <Badge variant="secondary">Revogada</Badge>
              ) : (
                <Badge variant="secondary">Nenhuma chave</Badge>
              )}
              {status?.keyPrefix && (
                <code className="rounded bg-muted px-2 py-0.5 text-sm">
                  {status.keyPrefix}…
                </code>
              )}
            </div>

            {status?.lastUsedAt && (
              <p className="text-sm text-muted-foreground">
                Último uso:{' '}
                {new Date(status.lastUsedAt).toLocaleString('pt-BR')}
              </p>
            )}

            {revealedKey && (
              <div className="rounded-lg border bg-muted p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    <span className="font-medium">API Key gerada</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyKey}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
                <p className="mt-2 break-all font-mono text-xs text-muted-foreground">
                  {revealedKey}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Esta chave só aparece uma vez. Salve no Lovable agora.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => generateMutation.mutate()}
                disabled={busy}
              >
                {generateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                <Key className="mr-2 h-4 w-4" />
                {status?.hasKey ? 'Gerar nova API key' : 'Gerar API key'}
              </Button>

              {status?.hasKey && status.isActive && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => revokeMutation.mutate()}
                  disabled={busy}
                >
                  {revokeMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Trash2 className="mr-2 h-4 w-4" />
                  Revogar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Documentação da API
            </CardTitle>
            <CardDescription>
              Base URL local:{' '}
              <Code>http://localhost:3000{API_BASE}</Code>. Em produção, troque
              o host. Todas as rotas usam o header{' '}
              <Code>x-api-key: &lt;sua-chave&gt;</Code> (não Bearer).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <section className="space-y-3">
              <h3 className="text-base font-semibold">Clientes</h3>
              <p className="text-sm text-muted-foreground">
                Lista clientes do tenant para vincular no sistema externo
                (Lovable).
              </p>
              <div className="rounded-lg border bg-muted/40 p-3 font-mono text-xs">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                  GET
                </span>{' '}
                {API_BASE}/clients
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Param</th>
                      <th className="py-2 pr-4 font-medium">Exemplo</th>
                      <th className="py-2 font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>q</Code>
                      </td>
                      <td className="py-2 pr-4">acme</td>
                      <td className="py-2">
                        Busca por nome, fantasia ou CNPJ (opcional)
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>limit</Code>
                      </td>
                      <td className="py-2 pr-4">50</td>
                      <td className="py-2">1–500 (default 100)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>offset</Code>
                      </td>
                      <td className="py-2 pr-4">0</td>
                      <td className="py-2">Paginação (opcional)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">
                        <Code>activeOnly</Code>
                      </td>
                      <td className="py-2 pr-4">true</td>
                      <td className="py-2">
                        Default true — use false para incluir inativos
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <pre className="overflow-x-auto rounded-lg border bg-muted p-3 text-xs leading-relaxed">
{`{
  "data": [
    {
      "id": "uuid",
      "name": "Razão Social",
      "tradeName": "Nome Fantasia",
      "cnpj": "00.000.000/0000-00"
    }
  ]
}`}
              </pre>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-semibold">Análises de boards</h3>
              <p className="text-sm text-muted-foreground">
                Análise final do board (Notion + docs + WhatsApp) e a parte de
                WhatsApp estruturada. Para buscar por cliente linkado no Lovable,
                use <Code>clientId</Code> (o cliente precisa ter board Notion
                vinculado no Asseto).
              </p>
              <div className="space-y-2">
                <div className="rounded-lg border bg-muted/40 p-3 font-mono text-xs">
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    GET
                  </span>{' '}
                  {API_BASE}/board-analyses
                </div>
                <div className="rounded-lg border bg-muted/40 p-3 font-mono text-xs">
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    GET
                  </span>{' '}
                  {API_BASE}/board-analyses/:id
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Param</th>
                      <th className="py-2 pr-4 font-medium">Exemplo</th>
                      <th className="py-2 font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>boardId</Code>
                      </td>
                      <td className="py-2 pr-4">uuid</td>
                      <td className="py-2">Filtrar por board Notion (opcional)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>clientId</Code>
                      </td>
                      <td className="py-2 pr-4">uuid</td>
                      <td className="py-2">
                        UUID do cliente Asseto — usa o board vinculado a ele
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>since</Code>
                      </td>
                      <td className="py-2 pr-4">2026-01-01T00:00:00.000Z</td>
                      <td className="py-2">
                        Só análises a partir desta data ISO (opcional)
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>limit</Code>
                      </td>
                      <td className="py-2 pr-4">50</td>
                      <td className="py-2">1–100 (default 20)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">
                        <Code>cursor</Code>
                      </td>
                      <td className="py-2 pr-4">ISO createdAt</td>
                      <td className="py-2">
                        Cursor da página anterior (opcional)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <pre className="overflow-x-auto rounded-lg border bg-muted p-3 text-xs leading-relaxed">
{`{
  "data": [
    {
      "id": "...",
      "notionBoardId": "...",
      "boardName": "Cliente X",
      "type": "summary",
      "result": "texto completo da análise final",
      "model": "gemini-...",
      "createdAt": "...",
      "metadata": { "taskCount": 12, "whatsappMessageCount": 40 },
      "whatsapp": {
        "id": "...",
        "messageCount": 40,
        "aiAnalysis": "resumo completo WhatsApp",
        "structuredSummary": {
          "behaviorProfile": "...",
          "recurringPendencies": [],
          "keyTopics": []
        },
        "createdAt": "..."
      }
    }
  ],
  "nextCursor": null
}`}
              </pre>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-semibold">
                Dados brutos (Notion + WhatsApp)
              </h3>
              <p className="text-sm text-muted-foreground">
                Tarefas e mensagens sem síntese de IA — pra quem faz o próprio
                scoring (ex: Health Score do Lovable) e precisa do dado cru em
                vez de um resumo já processado. Cobre os critérios{' '}
                <strong>objetivos</strong> (prazo, SLA, volume de interações,
                dependência do cliente) e <strong>subjetivos</strong> (tom,
                satisfação, clareza — inferidos do texto das mensagens). Não
                inclui dados financeiros/contratuais nem avaliação do gestor.
              </p>
              <div className="rounded-lg border bg-muted/40 p-3 font-mono text-xs">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                  GET
                </span>{' '}
                {API_BASE}/raw-data
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Param</th>
                      <th className="py-2 pr-4 font-medium">Exemplo</th>
                      <th className="py-2 font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>clientId</Code>
                      </td>
                      <td className="py-2 pr-4">uuid</td>
                      <td className="py-2">
                        UUID do cliente Asseto — usa o board vinculado a ele
                        (alternativa a <Code>boardId</Code>)
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>boardId</Code>
                      </td>
                      <td className="py-2 pr-4">uuid</td>
                      <td className="py-2">Board Notion direto (opcional)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">
                        <Code>since</Code>
                      </td>
                      <td className="py-2 pr-4">2026-01-01T00:00:00.000Z</td>
                      <td className="py-2">
                        Só mensagens de WhatsApp a partir desta data ISO
                        (opcional)
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">
                        <Code>limit</Code>
                      </td>
                      <td className="py-2 pr-4">500</td>
                      <td className="py-2">
                        Máximo de mensagens de WhatsApp (default/máx 1000)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <pre className="overflow-x-auto rounded-lg border bg-muted p-3 text-xs leading-relaxed">
{`{
  "board": { "id": "...", "name": "Cliente X" },
  "client": { "id": "...", "name": "Cliente X" },
  "tasks": {
    "count": 42,
    "items": [
      {
        "id": "...",
        "title": "Aprovar peça de campanha",
        "status": ["Em produção"],
        "column": "Produção",
        "dueDate": "2026-08-10",
        "clientApproval": null,
        "createdTime": "...",
        "lastEditedTime": "..."
      }
    ]
  },
  "whatsapp": {
    "count": 850,
    "messages": [
      {
        "id": "...",
        "createdAt": "...",
        "fromTeam": false,
        "senderName": "Fulano (Cliente)",
        "body": "Podem me mandar o material atualizado?",
        "replyToId": null,
        "hasMedia": false,
        "mediaType": null,
        "transcription": null,
        "imageDescription": null,
        "reactions": []
      }
    ]
  }
}`}
              </pre>
              <p className="text-xs text-muted-foreground">
                <Code>fromTeam</Code>: true = mensagem enviada por alguém da
                agência, false = pelo contato do cliente no grupo — é o que
                permite calcular SLA e volume de interação por lado.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold">Erros comuns</h3>
              <ul className="list-inside list-disc text-sm text-muted-foreground space-y-1">
                <li>
                  <Code>401</Code> — header ausente/errado, chave revogada ou
                  migration não aplicada
                </li>
                <li>
                  <Code>404</Code> — UUID de análise inexistente neste tenant
                </li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
